import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { useConfirm } from '../../ConfirmDialog';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * ACCESS (administrators only): who may see this device, in a sentence,
 * and the grants on this one device - where a one-off exception belongs.
 */
export default function AccessTab() {
  const { data, asset, meta, reload } = useOutletContext();
  const grants = data.grants || [];
  const readers = grants.filter((g) => g.level !== 'write').length;
  const writers = grants.filter((g) => g.level === 'write').length;
  return (
    <div className="as-tab as-tab-access">
      <section className="as-panel">
        <h2>Who sees this device</h2>
        <p className="as-sentence">
          Administrators may look at and change every device. A member of staff sees this one through
          the register&rsquo;s grants &mdash; by category, place or company, in Settings &rarr; Assets &amp; devices
          &rarr; Register access &mdash; or through a grant on this device alone, below.
          A contractor sees it only through a grant below{asset.holder_kind === 'contractor' ? ', or because they hold it' : ''}.
          Anybody whose ticket names it sees its card, and nothing more.
        </p>
        <p className="as-quiet">
          {grants.length === 0 ? 'Nobody has a grant on this device alone.'
            : `${grants.length} grant${grants.length === 1 ? '' : 's'} on this device alone: ${[
              writers ? `${writers} may change it` : null, readers ? `${readers} may look` : null].filter(Boolean).join(', ')}.`}
        </p>
      </section>
      <DeviceGrants asset={asset} grants={grants} users={meta?.users || []} onChanged={reload} />
    </div>
  );
}

/**
 * Grants on this one device, which is where a one-off exception belongs.
 * To a person, or - since 062 - to a contractor, who is then the one
 * contractor allowed to look at (or change) this device from their portal.
 * Moved here unchanged from pages/AssetDetail.js in the page redesign.
 */
export function DeviceGrants({ asset, grants, users, onChanged }) {
  const [who, setWho] = useState('user');
  const [userId, setUserId] = useState('');
  const [level, setLevel] = useState('read');
  const [error, setError] = useState('');
  const [contractors, setContractors] = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  useEffect(() => {
    axios.get(`${API_BASE}/asset-admin/contractors`)
      .then((r) => setContractors(r.data || []))
      .catch(() => setContractors(null));   // before 062: no contractors to choose
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API_BASE}/asset-admin/grants`,
        { [who === 'contractor' ? 'contractor_id' : 'user_id']: userId, level, asset_id: asset.id });
      setUserId('');
      onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the grant.');
    }
  };
  const remove = async (g) => {
    const name = g.contractor_name || g.user_name || g.email || 'this person';
    if (!(await confirm({ title: `Remove ${name}'s access to this device?`,
      message: g.level === 'write' ? 'They will no longer be able to look at or change it from here.'
        : 'They will no longer be able to look at it from here.',
      confirmLabel: 'Remove', danger: true }))) return;
    try { await axios.delete(`${API_BASE}/asset-admin/grants/${g.id}`); onChanged(); } catch (err) {
      setError(err.response?.data?.error || 'Could not remove the grant.');
    }
  };

  return (
    <section className="as-panel">
      <h2>Access to this device alone</h2>
      <p className="as-quiet">
        For a one-off: the server two people may change while everybody else may only look.
        Wider grants are in Settings → Assets & devices → Register access.
      </p>
      {error && <p className="as-error" role="alert">{error}</p>}
      {grants.length > 0 && (
        <ul className="as-tree">
          {grants.map((g) => (
            <li key={g.id}>
              <span className="as-chip" data-tone={g.level === 'write' ? 'warning' : 'info'}>
                {g.level === 'write' ? 'Change' : 'Look'}
              </span>
              <span>{g.contractor_name ? `${g.contractor_name} (contractor)` : (g.user_name || g.email)}</span>
              <span className="as-spacer" />
              <button type="button" className="btn btn-plain btn-small" onClick={() => remove(g)}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      <form className="as-inline-form" onSubmit={add}>
        {contractors && (
          <div className="form-group" style={{ flex: '0 1 9rem' }}>
            <label htmlFor="as-dg-who">Who</label>
            <select id="as-dg-who" value={who} onChange={(e) => { setWho(e.target.value); setUserId(''); }}>
              <option value="user">A person</option>
              <option value="contractor">A contractor</option>
            </select>
          </div>
        )}
        <div className="form-group">
          <label htmlFor="as-dg-user">{who === 'contractor' ? 'Contractor' : 'Person'}</label>
          <select id="as-dg-user" value={userId} onChange={(e) => setUserId(e.target.value)} required>
            <option value="">Choose…</option>
            {who === 'contractor'
              ? (contractors || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)
              : users.map((u) => <option key={u.id} value={u.id}>{u.display_name || u.email}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="as-dg-level">May</label>
          <select id="as-dg-level" value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="read">Look</option>
            <option value="write">Look and change</option>
          </select>
        </div>
        <button type="submit" className="btn btn-secondary">Grant</button>
      </form>
      {confirmDialog}
    </section>
  );
}
